/**
 * /admin/tools — Outils d'administration : seed démo, exports, maintenance.
 */
import React, { useState } from 'react';
import { Wrench, Sparkles, CheckCircle2, AlertCircle, RefreshCw, Database } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

export default function AdminToolsPage() {
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [seedError, setSeedError] = useState(null);

  const handleSeedDemo = async () => {
    if (seeding) return;
    if (!window.confirm('Créer le jeu de données de démonstration (1 prof, 1 élève, 1 leçon aujourd’hui) ? Opération idempotente.')) return;
    setSeeding(true);
    setSeedError(null);
    setSeedResult(null);
    try {
      const r = await api.boSeedDemo();
      setSeedResult(r);
      toast.success('Seed démo appliqué');
    } catch (e) {
      const msg = e.message || 'Erreur seed';
      setSeedError(msg);
      toast.error(msg);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-tools-page">
      <div className="mb-6">
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-violet-500 mb-1 flex items-center gap-2">
          <Wrench size={12} /> Admin · Outils
        </div>
        <h1 className="text-2xl sm:text-3xl font-black text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          Outils d’administration
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Maintenance, seed de données de test, exports. Réservé aux admins.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Seed démo */}
        <div className="rounded-2xl border-2 border-violet-500/30 bg-gradient-to-br from-violet-600/10 to-violet-500/5 p-5 sm:p-6" data-testid="seed-demo-panel">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-violet-600 text-white flex items-center justify-center shadow-lg shadow-violet-900/30 flex-shrink-0">
              <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-black text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Seed démo secrétariat
              </h2>
              <p className="text-sm text-muted-foreground mt-1 mb-3">
                Crée un enseignant démo (<code className="bg-muted px-1 rounded text-[11px]">demo.prof@code2roues.fr</code>), un élève démo et 1 leçon programmée aujourd’hui. Utile pour tester le back-office secrétariat.
              </p>
              <button
                onClick={handleSeedDemo}
                disabled={seeding}
                data-testid="btn-seed-demo"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-black text-sm shadow shadow-violet-900/30 transition disabled:opacity-60 disabled:cursor-wait"
              >
                {seeding ? (
                  <><RefreshCw className="w-4 h-4 animate-spin" /> Application…</>
                ) : (
                  <><Sparkles className="w-4 h-4" /> Appliquer le seed</>
                )}
              </button>
            </div>
          </div>

          {seedResult && (
            <details className="mt-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="seed-demo-result">
              <summary className="text-emerald-700 dark:text-emerald-300 text-sm font-black cursor-pointer flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Seed appliqué
              </summary>
              <pre className="mt-2 text-[11px] text-foreground/70 overflow-auto max-h-48">
                {JSON.stringify(seedResult, null, 2)}
              </pre>
            </details>
          )}

          {seedError && (
            <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-600 dark:text-red-300 flex items-start gap-2" data-testid="seed-demo-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>{seedError}</div>
            </div>
          )}
        </div>

        {/* Placeholder future : exports */}
        <div className="rounded-2xl border-2 border-dashed border-border p-5 opacity-60" data-testid="tools-future-panel">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0">
              <Database className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-black text-muted-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Exports CSV / Sauvegarde DB
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Bientôt : export CSV des élèves, backup MongoDB, purge des logs anciens.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
